// src/pages/RiskManagement.jsx
// Comprehensive Risk Management Page - State-of-the-art client retention interface

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  Users,
  Settings,
  ChevronRight,
  RefreshCw,
  Target,
  BarChart3,
  CheckCircle2,
  XCircle,
  Bell,
  Clock,
  AlertCircle,
  Phone,
  Mail,
  MessageSquare,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

// Import new risk components
import {
  RISK_CATEGORIES,
  getRiskCategory,
  RiskDistributionPanel,
  ClientRiskQueue,
  ClientRiskDetailDrawer,
  InterventionPanel,
  FounderMetricsBanner
} from '../components/risk';

const BASE = process.env.REACT_APP_API_URL || "";

// Tabs shown to admin/founder roles
const ADMIN_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'alerts', label: 'Alerts', icon: Bell },
  { id: 'factors', label: 'Risk Factors', icon: AlertTriangle },
  { id: 'settings', label: 'Settings', icon: Settings }
];

// Tabs shown to agents
const AGENT_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'alerts', label: 'Alerts', icon: Bell },
  { id: 'settings', label: 'Settings', icon: Settings }
];

export default function RiskManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [factors, setFactors] = useState(null);
  const [atRiskClients, setAtRiskClients] = useState([]);
  const [clientsTotal, setClientsTotal] = useState(null); // Actual total from server
  const [alerts, setAlerts] = useState({ alerts: [], counts: {} });
  const [interventionMetrics, setInterventionMetrics] = useState(null);

  // UI states for drawer and modals
  const [selectedClient, setSelectedClient] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [interventionOpen, setInterventionOpen] = useState(false);
  const [interventionType, setInterventionType] = useState('call');
  const [categoryFilter, setCategoryFilter] = useState(null);

  // Fetch user role
  useEffect(() => {
    async function fetchUserRole() {
      try {
        const res = await fetch(`${BASE}/auth/me`, { credentials: "include" });
        const json = await res.json();
        if (res.ok && json.ok) {
          setUserRole(json.user?.role);
        }
      } catch (err) {
        console.error("[RiskManagement] Failed to fetch user role:", err);
      }
    }
    fetchUserRole();
  }, []);

  const loadData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [dashboardRes, factorsRes, clientsRes, alertsRes, metricsRes] = await Promise.all([
        fetch(`${BASE}/risk/dashboard`, { credentials: "include" }).then(r => r.json()),
        fetch(`${BASE}/risk/factors`, { credentials: "include" }).then(r => r.json()),
        fetch(`${BASE}/risk/at-risk-clients?limit=1000&minScore=0`, { credentials: "include" }).then(r => r.json()),
        fetch(`${BASE}/churn-prediction/alerts?status=active&limit=100`, { credentials: "include" }).then(r => r.json()),
        fetch(`${BASE}/risk/intervention-metrics`, { credentials: "include" }).then(r => r.json()).catch(() => ({}))
      ]);

      if (dashboardRes.ok) setDashboard(dashboardRes);
      if (factorsRes.ok) setFactors(factorsRes);
      if (clientsRes.ok) {
        setAtRiskClients(clientsRes.clients || []);
        setClientsTotal(clientsRes.total || null);
      }
      if (alertsRes.ok) setAlerts({ alerts: alertsRes.alerts || [], counts: alertsRes.counts || {} });
      if (metricsRes.ok) setInterventionMetrics(metricsRes);

      setError(null);
    } catch (err) {
      console.error('Failed to load risk data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle clientId query param - auto-open client drawer when navigating from Morning Brief
  useEffect(() => {
    const clientId = searchParams.get('clientId');
    if (clientId && atRiskClients.length > 0) {
      const client = atRiskClients.find(c => c.id === clientId);
      if (client) {
        setSelectedClient(client);
        setDrawerOpen(true);
        // Clear the clientId param to avoid re-opening on refresh
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('clientId');
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [searchParams, atRiskClients, setSearchParams]);

  const setTab = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  // Handle client selection for drawer
  const handleClientClick = (client) => {
    setSelectedClient(client);
    setDrawerOpen(true);
  };

  // Handle intervention initiation
  const handleIntervention = (type, client) => {
    setSelectedClient(client);
    setInterventionType(type);
    setInterventionOpen(true);
    setDrawerOpen(false);
  };

  // Handle intervention completion
  const handleInterventionComplete = (result) => {
    // Refresh data after intervention
    loadData(true);
    // Close intervention modal
    setInterventionOpen(false);
  };

  // Handle score refresh
  const handleRefreshScore = async (clientId) => {
    try {
      await fetch(`${BASE}/churn-prediction/recalculate/${clientId}`, {
        method: 'POST',
        credentials: 'include'
      });
      await loadData(true);
    } catch (err) {
      console.error('Failed to refresh score:', err);
    }
  };

  // Navigate to client profile
  const handleNavigateToClient = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  // Quick action handlers
  const handleQuickSMS = (client) => handleIntervention('sms', client);
  const handleQuickCall = (client) => handleIntervention('call', client);
  const handleQuickEmail = (client) => handleIntervention('email', client);

  const isAdmin = userRole && ['admin', 'agency_admin', 'agency_owner', 'fmo', 'founder'].includes(userRole);
  const TABS = isAdmin ? ADMIN_TABS : AGENT_TABS;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-lg">Loading Risk Management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-red-900/30 border border-red-500/50 rounded-2xl p-8 max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error Loading Data</h2>
          <p className="text-slate-400 mb-4">{error}</p>
          <button
            onClick={() => loadData()}
            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate distribution for the panel
  const distribution = dashboard?.distribution || {};
  // Use the total from backend directly, not summing all values (which would double count)
  const totalClients = parseInt(distribution.total) || 0;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <Shield className="w-8 h-8 text-amber-400" />
                Risk Management
              </h1>
              <p className="text-slate-400 text-sm mt-1 hidden sm:block">
                Monitor your book of business and manage client outreach
              </p>
            </div>
            <button
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 text-slate-300 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-white text-sm">Refresh</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.id === 'alerts' && alerts.counts?.total > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                      {alerts.counts.total}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Overview Tab - Revamped */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Founder Metrics Banner (for admins) */}
            {isAdmin && interventionMetrics && (
              <FounderMetricsBanner
                savedClientsThisMonth={interventionMetrics.savedClientsThisMonth || 0}
                savedClientsLastMonth={interventionMetrics.savedClientsLastMonth || 0}
                totalInterventions={interventionMetrics.totalInterventions || 0}
                successRate={interventionMetrics.successRate || 0}
                avgRiskScoreAtIntervention={interventionMetrics.avgRiskScoreAtIntervention || 0}
              />
            )}

            {/* Hero Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <HeroCard
                icon={<Users className="w-6 h-6" />}
                label="Total Clients"
                value={totalClients.toLocaleString()}
                color="blue"
                onClick={() => navigate('/clients')}
              />
              <HeroCard
                icon={<AlertTriangle className="w-6 h-6" />}
                label="At Risk (50+)"
                value={(parseInt(distribution.elevated || 0) + parseInt(distribution.high || 0) + parseInt(distribution.critical || 0) + parseInt(distribution.severe || 0)).toString()}
                subtitle="Needs attention"
                color="amber"
                onClick={() => navigate('/clients?risk_category=elevated')}
              />
              <HeroCard
                icon={<Target className="w-6 h-6" />}
                label="High Risk (65+)"
                value={(parseInt(distribution.high || 0) + parseInt(distribution.critical || 0) + parseInt(distribution.severe || 0)).toString()}
                subtitle="Priority outreach"
                color="red"
                onClick={() => navigate('/clients?risk_category=high')}
              />
              <HeroCard
                icon={<Bell className="w-6 h-6" />}
                label="Active Alerts"
                value={(alerts.counts?.emergency || 0) + (alerts.counts?.urgent || 0) + (alerts.counts?.warning || 0)}
                subtitle="Requires action"
                color="orange"
                onClick={() => setTab('alerts')}
              />
            </div>

            {/* Two column layout */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left column - Distribution */}
              <div className="lg:col-span-1">
                <RiskDistributionPanel
                  distribution={distribution}
                  totalClients={totalClients}
                  onCategoryClick={(cat) => {
                    setCategoryFilter(cat === categoryFilter ? null : cat);
                  }}
                  selectedCategory={categoryFilter}
                />
              </div>

              {/* Right column - Client Queue */}
              <div className="lg:col-span-2">
                <ClientRiskQueue
                  clients={atRiskClients}
                  totalClients={clientsTotal}
                  onClientClick={handleClientClick}
                  onQuickSMS={handleQuickSMS}
                  onQuickCall={handleQuickCall}
                  onQuickEmail={handleQuickEmail}
                  selectedClientId={selectedClient?.id}
                  filterCategory={categoryFilter}
                />
              </div>
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <AlertsTab
            alerts={alerts}
            navigate={navigate}
            onRefresh={() => loadData(true)}
          />
        )}

        {/* Factors Tab */}
        {activeTab === 'factors' && (
          <FactorsTab
            factors={factors}
            isAdmin={isAdmin}
            onRefresh={() => loadData(true)}
          />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsTab
            isAdmin={isAdmin}
            userRole={userRole}
          />
        )}
      </main>

      {/* Client Risk Detail Drawer */}
      <ClientRiskDetailDrawer
        client={selectedClient}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          // Refresh the list to show updated scores after viewing client details
          // The detail view calculates fresh scores and updates the database
          loadData(true);
        }}
        onIntervention={handleIntervention}
        onNavigateToClient={handleNavigateToClient}
        onRefreshScore={handleRefreshScore}
      />

      {/* Intervention Modal */}
      <InterventionPanel
        isOpen={interventionOpen}
        onClose={() => setInterventionOpen(false)}
        client={selectedClient}
        initialType={interventionType}
        onInterventionComplete={handleInterventionComplete}
      />
    </div>
  );
}

// ============================================================================
// ALERTS TAB
// ============================================================================

function AlertsTab({ alerts, navigate, onRefresh }) {
  const [filter, setFilter] = useState('all');
  const [dismissing, setDismissing] = useState(null);
  const [dismissingAll, setDismissingAll] = useState(false);

  const filteredAlerts = alerts.alerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.alert_type === filter;
  });

  const handleDismissAll = async () => {
    if (filteredAlerts.length === 0) return;
    setDismissingAll(true);
    try {
      await Promise.all(
        filteredAlerts.map(alert =>
          fetch(`${BASE}/churn-prediction/alerts/${alert.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'dismiss' })
          })
        )
      );
      onRefresh();
    } catch (err) {
      console.error('Failed to dismiss all alerts:', err);
    } finally {
      setDismissingAll(false);
    }
  };

  const handleDismiss = async (alertId) => {
    setDismissing(alertId);
    try {
      const response = await fetch(`${BASE}/churn-prediction/alerts/${alertId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'dismiss' })
      });
      if (response.ok) {
        onRefresh();
      } else {
        const data = await response.json().catch(() => ({}));
        console.error('Failed to dismiss alert:', data.error || response.status);
      }
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
    } finally {
      setDismissing(null);
    }
  };

  const getAlertStyle = (type) => {
    switch (type) {
      case 'emergency':
        return { bg: 'bg-red-900/30', border: 'border-red-500/50', text: 'text-red-400', icon: AlertTriangle };
      case 'urgent':
        return { bg: 'bg-orange-900/30', border: 'border-orange-500/50', text: 'text-orange-400', icon: AlertCircle };
      default:
        return { bg: 'bg-yellow-900/30', border: 'border-yellow-500/50', text: 'text-yellow-400', icon: Bell };
    }
  };

  return (
    <div className="space-y-6">
      {/* Alert counts summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => setFilter('emergency')}
          className={`p-4 rounded-xl border transition-colors ${
            filter === 'emergency' ? 'bg-red-900/40 border-red-500' : 'bg-slate-800/50 border-slate-700 hover:border-red-500/50'
          }`}
        >
          <div className="text-3xl font-bold text-red-400">{alerts.counts?.emergency || 0}</div>
          <div className="text-sm text-slate-400">Emergency</div>
        </button>
        <button
          onClick={() => setFilter('urgent')}
          className={`p-4 rounded-xl border transition-colors ${
            filter === 'urgent' ? 'bg-orange-900/40 border-orange-500' : 'bg-slate-800/50 border-slate-700 hover:border-orange-500/50'
          }`}
        >
          <div className="text-3xl font-bold text-orange-400">{alerts.counts?.urgent || 0}</div>
          <div className="text-sm text-slate-400">Urgent</div>
        </button>
        <button
          onClick={() => setFilter('warning')}
          className={`p-4 rounded-xl border transition-colors ${
            filter === 'warning' ? 'bg-yellow-900/40 border-yellow-500' : 'bg-slate-800/50 border-slate-700 hover:border-yellow-500/50'
          }`}
        >
          <div className="text-3xl font-bold text-yellow-400">{alerts.counts?.warning || 0}</div>
          <div className="text-sm text-slate-400">Warning</div>
        </button>
      </div>

      {/* Filter toggle + Dismiss All */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          {['all', 'emergency', 'urgent', 'warning'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              {f === 'all' ? 'All Alerts' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {filteredAlerts.length > 0 && (
          <button
            onClick={handleDismissAll}
            disabled={dismissingAll}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700 hover:border-slate-500 transition-colors disabled:opacity-50"
          >
            {dismissingAll ? 'Dismissing...' : `Dismiss All${filter !== 'all' ? ` ${filter.charAt(0).toUpperCase() + filter.slice(1)}` : ''}`}
          </button>
        )}
      </div>

      {/* Alerts list */}
      <div className="space-y-3">
        {filteredAlerts.length === 0 ? (
          <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-3" />
            <p className="text-slate-400">No {filter !== 'all' ? filter : ''} alerts at this time</p>
          </div>
        ) : (
          filteredAlerts.map(alert => {
            const style = getAlertStyle(alert.alert_type);
            const IconComponent = style.icon;
            const clientName = `${alert.first_name || ''} ${alert.last_name || ''}`.trim() || 'Unknown';

            return (
              <div
                key={alert.id}
                className={`p-4 rounded-xl border ${style.bg} ${style.border}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${style.bg}`}>
                    <IconComponent className={`w-5 h-5 ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${style.text} ${style.bg}`}>
                          {alert.alert_type?.toUpperCase()}
                        </span>
                        <span className="font-medium text-white">{clientName}</span>
                      </div>
                      {alert.response_due_at && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          Due: {new Date(alert.response_due_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-slate-300 mt-1">{alert.alert_title || alert.alert_message}</p>
                    {alert.recommended_action && (
                      <p className="text-sm text-amber-400 mt-2">Action: {alert.recommended_action}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/clients/${alert.client_id}`)}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDismiss(alert.id)}
                      disabled={dismissing === alert.id}
                      className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-slate-300 text-sm rounded-lg transition-colors disabled:opacity-50"
                    >
                      {dismissing === alert.id ? '...' : 'Dismiss'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FACTORS TAB
// ============================================================================

function FactorsTab({ factors, isAdmin, onRefresh }) {
  const factorList = factors?.factors || [];

  // Group factors by category
  const groupedFactors = factorList.reduce((acc, factor) => {
    const cat = factor.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(factor);
    return acc;
  }, {});

  const categoryLabels = {
    engagement: { label: 'Engagement', color: 'blue' },
    utilization: { label: 'Utilization', color: 'purple' },
    benefit_fit: { label: 'Benefit Fit', color: 'pink' },
    life_events: { label: 'Life Events', color: 'amber' },
    external: { label: 'External', color: 'cyan' },
    other: { label: 'Other', color: 'slate' }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Risk Factor Weights</h3>
        <p className="text-slate-400 text-sm mb-6">
          These factors contribute to each client's overall risk score. Higher weighted categories have more impact on the final score.
        </p>

        <div className="space-y-6">
          {Object.entries(groupedFactors).map(([category, catFactors]) => {
            const config = categoryLabels[category] || categoryLabels.other;
            return (
              <div key={category} className="border-b border-slate-700 pb-4 last:border-0 last:pb-0">
                <h4 className={`font-medium text-${config.color}-400 mb-3`}>{config.label}</h4>
                <div className="space-y-2">
                  {catFactors.map((factor, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-700/30"
                    >
                      <div>
                        <div className="text-white">{factor.name}</div>
                        {factor.description && (
                          <div className="text-xs text-slate-500">{factor.description}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-sm ${factor.base_points > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {factor.base_points > 0 ? '+' : ''}{factor.base_points} pts
                        </span>
                        {factor.weight && (
                          <span className="text-xs text-slate-500">
                            {(factor.weight * 100).toFixed(0)}% weight
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isAdmin && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-400 mb-2">
            <Settings className="w-5 h-5" />
            <span className="font-medium">Admin: Factor Weights</span>
          </div>
          <p className="text-sm text-slate-400">
            Factor weights can be adjusted in the Founder Analytics dashboard. Changes affect all future score calculations.
          </p>
          <button
            onClick={() => window.location.href = '/founder-analytics'}
            className="mt-3 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm font-medium transition-colors"
          >
            Go to Founder Analytics
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SETTINGS TAB
// ============================================================================

function SettingsTab({ isAdmin, userRole }) {
  const [bulkScoring, setBulkScoring] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const handleBulkScoring = async () => {
    setBulkScoring(true);
    setBulkResult(null);
    setBulkProgress({ percent: 0, status: 'Dispatching...', pending: 0, inFlight: 0, scored: 0 });

    try {
      // Step 1: Dispatch all clients to SQS queue
      const dispatchRes = await fetch(`${BASE}/risk/dispatch-scoring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!dispatchRes.ok) {
        const err = await dispatchRes.json();
        throw new Error(err.error || 'Failed to dispatch scoring');
      }

      const dispatchData = await dispatchRes.json();
      console.log('Dispatch result:', dispatchData);

      setBulkProgress({
        percent: 5,
        status: 'Processing in background...',
        total: dispatchData.totalClients,
        pending: dispatchData.batchesQueued,
        inFlight: 0,
        scored: 0
      });

      // Step 2: Poll queue status until complete
      let complete = false;
      let lastScored = 0;

      while (!complete) {
        await new Promise(r => setTimeout(r, 3000)); // Poll every 3 seconds

        const statusRes = await fetch(`${BASE}/risk/scoring-queue-status`, {
          credentials: 'include'
        });

        if (statusRes.ok) {
          const status = await statusRes.json();
          const totalPending = status.queue.pending + status.queue.inFlight;
          const scored = status.recent.scoredLastHour || 0;
          const percentDone = dispatchData.batchesQueued > 0
            ? Math.min(95, Math.round(((dispatchData.batchesQueued - totalPending) / dispatchData.batchesQueued) * 100))
            : 100;

          setBulkProgress({
            percent: percentDone,
            status: status.status === 'IDLE' ? 'Complete!' : `Processing... ${status.estimatedRemaining || ''}`,
            total: dispatchData.totalClients,
            pending: status.queue.pending,
            inFlight: status.queue.inFlight,
            scored: scored
          });

          // Check if complete
          if (status.status === 'IDLE' && scored > lastScored) {
            complete = true;
          }
          lastScored = scored;

          // Safety timeout after 10 minutes of no progress
          if (scored === lastScored && totalPending === 0) {
            complete = true;
          }
        }
      }

      setBulkProgress(prev => ({ ...prev, percent: 100, status: 'Complete!' }));
      setBulkResult({
        success: true,
        message: `Scoring complete! ${dispatchData.totalClients} clients processed via scalable queue.`
      });
    } catch (err) {
      setBulkResult({ error: err.message });
    } finally {
      setBulkScoring(false);
      setBulkProgress(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Bulk Scoring Progress Modal */}
      {bulkScoring && bulkProgress && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-600 p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Scoring All Clients</h3>

            {/* Progress bar */}
            <div className="w-full bg-slate-700 rounded-full h-4 mb-3">
              <div
                className="bg-amber-500 h-4 rounded-full transition-all duration-300"
                style={{ width: `${bulkProgress.percent}%` }}
              />
            </div>

            {/* Stats */}
            <div className="text-center mb-4">
              <div className="text-2xl font-bold text-white">{bulkProgress.percent}%</div>
              <div className="text-amber-400 font-medium">{bulkProgress.status}</div>
              {bulkProgress.total > 0 && (
                <div className="text-slate-400 text-sm mt-1">
                  {bulkProgress.total.toLocaleString()} total clients
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-xs sm:text-sm">
              <div className="bg-slate-900/50 rounded p-2">
                <div className="text-blue-400 font-medium">{bulkProgress.pending || 0}</div>
                <div className="text-slate-500 text-xs">Queued</div>
              </div>
              <div className="bg-slate-900/50 rounded p-2">
                <div className="text-yellow-400 font-medium">{bulkProgress.inFlight || 0}</div>
                <div className="text-slate-500 text-xs">Processing</div>
              </div>
              <div className="bg-slate-900/50 rounded p-2">
                <div className="text-green-400 font-medium">{bulkProgress.scored || 0}</div>
                <div className="text-slate-500 text-xs">Scored</div>
              </div>
            </div>

            <p className="text-slate-500 text-xs mt-4 text-center">
              Using scalable queue system. You can close this page - scoring continues in background.
            </p>
          </div>
        </div>
      )}

      {/* User info */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Your Settings</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <span className="text-slate-400">Current Role</span>
            <span className="text-white font-medium capitalize">{userRole || 'Unknown'}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-slate-400">Admin Access</span>
            <span className={`font-medium ${isAdmin ? 'text-green-400' : 'text-slate-500'}`}>
              {isAdmin ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Admin-only actions */}
      {isAdmin && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Admin Actions</h3>

          {/* Bulk Scoring */}
          <div className="p-4 bg-slate-900/50 rounded-lg">
            <h4 className="font-medium text-white mb-2">Bulk Risk Scoring</h4>
            <p className="text-sm text-slate-400 mb-4">
              Recalculate risk scores for all clients. Processes 50 clients at a time with progress updates.
            </p>
            <button
              onClick={handleBulkScoring}
              disabled={bulkScoring}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-slate-900 font-medium rounded-lg transition-colors disabled:text-slate-500"
            >
              {bulkScoring ? 'Running...' : 'Run Bulk Scoring'}
            </button>
            {bulkResult && (
              <div className={`mt-3 p-3 rounded-lg ${bulkResult.error ? 'bg-red-900/30 text-red-400' : 'bg-green-900/30 text-green-400'}`}>
                {bulkResult.error || bulkResult.message}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notification preferences placeholder */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Notifications</h3>
        <p className="text-slate-400 text-sm">
          Alert notification preferences are managed in your account settings.
        </p>
        <button
          onClick={() => window.location.href = '/settings'}
          className="mt-3 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
        >
          Go to Settings
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function HeroCard({ icon, label, value, subtitle, color, onClick }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30',
    orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30'
  };

  const iconColors = {
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    green: 'text-green-400',
    orange: 'text-orange-400'
  };

  return (
    <button
      onClick={onClick}
      className={`p-4 sm:p-6 rounded-xl bg-gradient-to-br ${colorClasses[color]} border hover:scale-[1.02] transition-transform text-left w-full`}
    >
      <div className={`${iconColors[color]} mb-2`}>{icon}</div>
      <div className="text-2xl sm:text-3xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
    </button>
  );
}
